import PropTypes from 'prop-types';
import Head from 'next/head';
import { QueryClient, dehydrate } from '@tanstack/react-query';
// Components
import PageResidence from '../../components/templates/PageResidence/PageResidence';

// Hooks
import useSeoLinks from '../../src/hooks/useSeoLinks';

// Utils
import { completePageSlug, replacePageSlug } from '../../src/utils/pageSlug';
import getServerSideTranslations from '../../src/utils/server-side';
import getServerPageResidence from '../../src/utils/getServerPageResidence';

// Constants
import {
  LOCALE_EN, LOCALE_FR, URL_SLUGS_RESIDENCES,
} from '../../src/constants';

// Services
import { getContentTranslationProxy } from '../../src/services/content-translations';

function Index({
  page, relatedPageSlug, locale, localeRelatedPage,
}) {
  const slugPage = page?.[0]?.data?.slug;

  const slugs = {
    [locale]: replacePageSlug(slugPage, locale),
    [localeRelatedPage]: relatedPageSlug
      ? replacePageSlug(relatedPageSlug, localeRelatedPage) : null,
  };

  const { canonicalUrl } = useSeoLinks(URL_SLUGS_RESIDENCES[locale], slugs);
  return (
    <>
      <Head>
        <link rel="canonical" href={`${canonicalUrl}`} />
      </Head>

      <PageResidence
        page={page}
        slugs={slugs}
      />
    </>
  );
}
export async function getServerSideProps({ locale, query, resolvedUrl }) {
  const slug = completePageSlug(query?.slug, locale);
  const translations = await getServerSideTranslations(locale);
  const preview = {
    preview: query.previewMode ? true : null,
    contentId: query.contentId || null,
    token: query.previewMode,
  };

  const queryClient = new QueryClient();
  const page = await getServerPageResidence(queryClient, slug, locale, resolvedUrl, preview);

  if (page?.redirect || page?.notFound) return page;

  const localeRelatedPage = locale === LOCALE_FR ? LOCALE_EN : LOCALE_FR;
  const relatedPage = await queryClient.fetchQuery(
    ['page', { contentId: page?.[0]?.contentId, locale: localeRelatedPage }],
    () => getContentTranslationProxy({ contentId: page?.[0]?.contentId }, localeRelatedPage),
    { enabled: !!page?.[0]?.contentId },
  );
  const relatedPageSlug = relatedPage?.data?.[0]?.data?.slug || '';

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      page,
      relatedPageSlug,
      localeRelatedPage,
      slug: query.slug,
      ...translations,
    },
  };
}

Index.propTypes = {
  page: PropTypes.arrayOf(PropTypes.shape({
    data: PropTypes.shape({
      blocks: PropTypes.arrayOf(PropTypes.shape({
        type: PropTypes.string,
        data: PropTypes.shape({}),
      })),
      slug: PropTypes.string,
    }),
  })).isRequired,
  relatedPageSlug: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
  localeRelatedPage: PropTypes.string.isRequired,
};

export default Index;
